import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { EMPTY, Observable, Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { User } from '../../core/models';
import { ModalService } from '../../shared/modal';
import {
  NotificationService,
  NotificationSettings,
  NotificationSettingsPatch,
  SessionService,
  SettingsService,
  TwoFactorStatus,
} from '../../core/services';
import { apiErrorMessage } from '../../core/utils/api-error-message';

import { currencySymbol } from '../../core/utils/currency-symbol';
import { initials } from '../../core/utils/initials';
import { AvatarModal } from './modals/avatar/avatar-modal';
import { BackupCodesModal } from './modals/backup-codes/backup-codes-modal';
import { ChangePasswordModal } from './modals/change-password/change-password-modal';
import { DisableTwoFactorModal } from './modals/disable-two-factor/disable-two-factor-modal';
import { TwoFactorSetupModal } from './modals/two-factor-setup/two-factor-setup-modal';

/** Cents (int) <-> the plain decimal a person types into an amount field. */
function toCents(value: number | string | null): number | null {
  if (value === null || value === undefined || value === '') return null;
  return Math.round(Number(value) * 100);
}
function toAmount(cents: number | null): number | null {
  return cents === null || cents === undefined ? null : cents / 100;
}

@Component({
  selector: 'app-settings',
  standalone: false,
  templateUrl: './settings.html',
  styleUrl: './settings.scss',
})
export class Settings implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  readonly user$: Observable<User | null>;
  readonly initials = initials;
  readonly currencySymbol = currencySymbol;

  isSavingProfile = false;

  profileForm: FormGroup;
  notificationsForm: FormGroup;

  isLoadingNotifications = true;
  isSavingNotifications = false;
  notificationsCurrency = 'usd';

  // Two-factor authentication
  isLoadingTwoFactorStatus = true;
  twoFactorStatus: TwoFactorStatus | null = null;

  constructor(
    private fb: FormBuilder,
    private settingsService: SettingsService,
    private sessionService: SessionService,
    private notificationService: NotificationService,
    private router: Router,
    private modalService: ModalService,
  ) {
    this.user$ = this.sessionService.user$;

    this.profileForm = this.fb.group({
      name: ['', Validators.required],
      email: [{ value: '', disabled: true }],
    });

    this.notificationsForm = this.fb.group({
      low_balance_alerts: [false],
      low_balance_threshold: [null],
      payment_receipts: [false],
      usage_alerts: [false],
      usage_alert_threshold: [null],
    });

    // authGuard already resolved /me before this page can render, so this fires
    // immediately with the real user no separate fetch needed here.
    this.sessionService.user$
      .pipe(takeUntil(this._destroy$))
      .subscribe(user => {
        if (!user) return;
        this.profileForm.patchValue({ name: user.name, email: user.email });
      });
  }

  ngOnInit(): void {
    this.loadNotifications();
    this.loadTwoFactorStatus();
  }

  get twoFactorStatusLabel(): string {
    const status = this.twoFactorStatus;
    if (!status) return '';
    const enabledOn = status.enabledAt
      ? ` on ${new Date(status.enabledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
      : '';
    const codeWord = status.backupCodesRemaining === 1 ? 'code' : 'codes';
    return `Enabled${enabledOn} · ${status.backupCodesRemaining} backup ${codeWord} remaining`;
  }

  private errorMessage(error: unknown, fallback: string): string {
    return apiErrorMessage(error, fallback);
  }

  saveProfile(): void {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      this.notificationService.error('Enter a name before saving.');
      return;
    }

    this.isSavingProfile = true;
    // .value already excludes the disabled email control and is keyed as { name }
    //  no manual remapping needed, same as notificationsForm.
    this.settingsService.updateProfile(this.profileForm.value)
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.notificationService.error(this.errorMessage(error, 'An error occurred while updating your profile. Please try again later.'));
          return throwError(() => error);
        }),
        tap(user => {
          this.sessionService.patchUser(user);
          this.notificationService.success('Profile updated successfully');
        }),
        finalize(() => { this.isSavingProfile = false; }),
      ).subscribe();
  }

  // ── Dialogs ───────────────────────────────────────────────────
  // Each dialog is its own modal component under ./modals; they do their own
  // request, pending state and inline errors, and close with a result only
  // once the change has actually been saved.

  openAvatarDialog(): void {
    this.modalService.small(AvatarModal, { data: { name: this.sessionService.user?.name ?? null } });
  }

  openPasswordDialog(): void {
    this.modalService.small(ChangePasswordModal);
  }

  confirmDeleteAccount(): void {
    this.modalService.confirm({
      title: 'Delete account?',
      message: 'This will permanently delete your account and all associated data. This action cannot be undone.',
      confirmLabel: 'Delete account',
      pendingLabel: 'Deleting…',
      danger: true,
      onConfirm: () => this.settingsService.deleteAccount().pipe(
        takeUntil(this._destroy$),
        tap(() => {
          this.notificationService.success('Your account has been deleted');
          this.router.navigate(['/login']);
        }),
      ),
      errorMessage: error => this.errorMessage(error, 'An error occurred while deleting your account. Please try again later.'),
    }).subscribe();
  }

  // ── Notifications ─────────────────────────────────────────────

  private loadNotifications(): void {
    this.isLoadingNotifications = true;
    this.settingsService.getNotifications()
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.notificationService.error(this.errorMessage(error, 'Could not load your notification settings.'));
          return EMPTY;
        }),
        tap(settings => this.applyNotificationSettings(settings)),
        finalize(() => { this.isLoadingNotifications = false; }),
      ).subscribe();
  }

  private applyNotificationSettings(settings: NotificationSettings): void {
    this.notificationsCurrency = settings.currency;
    this.notificationsForm.reset({
      low_balance_alerts: settings.lowBalanceAlerts,
      low_balance_threshold: toAmount(settings.lowBalanceThresholdCents),
      payment_receipts: settings.paymentReceipts,
      usage_alerts: settings.usageAlerts,
      usage_alert_threshold: toAmount(settings.usageAlertThresholdCents),
    });
  }

  saveNotifications(): void {
    if (this.isSavingNotifications || !this.notificationsForm.dirty) return;

    const { low_balance_alerts, low_balance_threshold, payment_receipts, usage_alerts, usage_alert_threshold } = this.notificationsForm.value;

    // Mirrors the server's own checks avoids a round trip for the two most common mistakes.
    if (usage_alerts && (usage_alert_threshold === null || usage_alert_threshold === '')) {
      this.notificationService.error('Enter a usage alert threshold before turning usage alerts on.');
      return;
    }
    if ((low_balance_threshold !== null && low_balance_threshold < 0) || (usage_alert_threshold !== null && usage_alert_threshold < 0)) {
      this.notificationService.error('Alert thresholds cannot be negative.');
      return;
    }

    const controls = this.notificationsForm.controls;
    const patch: NotificationSettingsPatch = {};
    if (controls['low_balance_alerts'].dirty) patch.low_balance_alerts = low_balance_alerts;
    if (controls['low_balance_threshold'].dirty) patch.low_balance_threshold_cents = toCents(low_balance_threshold);
    if (controls['payment_receipts'].dirty) patch.payment_receipts = payment_receipts;
    if (controls['usage_alerts'].dirty) patch.usage_alerts = usage_alerts;
    if (controls['usage_alert_threshold'].dirty) patch.usage_alert_threshold_cents = toCents(usage_alert_threshold);

    this.isSavingNotifications = true;
    this.settingsService.updateNotifications(patch)
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.notificationService.error(this.errorMessage(error, 'An error occurred while updating your notification settings.'));
          return throwError(() => error);
        }),
        tap(settings => {
          this.applyNotificationSettings(settings);
          this.notificationService.success('Notification settings updated');
        }),
        finalize(() => { this.isSavingNotifications = false; }),
      ).subscribe();
  }

  // ── Two-factor authentication ─────────────────────────────────

  private loadTwoFactorStatus(): void {
    this.isLoadingTwoFactorStatus = true;
    this.settingsService.getTwoFactorStatus()
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.notificationService.error(this.errorMessage(error, 'Could not load two-factor authentication status.'));
          return EMPTY;
        }),
        tap(status => { this.twoFactorStatus = status; }),
        finalize(() => { this.isLoadingTwoFactorStatus = false; }),
      ).subscribe();
  }

  openEnableSetupDialog(): void {
    this.modalService.small(TwoFactorSetupModal)
      .closed$.pipe(takeUntil(this._destroy$))
      .subscribe(backupCodes => {
        if (!backupCodes) return; // dismissed nothing was enabled
        this.twoFactorStatus = { enabled: true, enabledAt: new Date().toISOString(), backupCodesRemaining: backupCodes.length };
        this.sessionService.patchUser({ twoFactorEnabled: true });
        this.notificationService.success('Two-factor authentication is now enabled.');
        this.modalService.small(BackupCodesModal, { data: { codes: backupCodes }, closeOnBackdropClick: false });
      });
  }

  openDisableDialog(): void {
    this.modalService.small(DisableTwoFactorModal)
      .closed$.pipe(takeUntil(this._destroy$))
      .subscribe(disabled => {
        if (!disabled) return;
        this.twoFactorStatus = { enabled: false, enabledAt: null, backupCodesRemaining: 0 };
        this.sessionService.patchUser({ twoFactorEnabled: false });
        this.notificationService.success('Two-factor authentication has been disabled.');
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
